import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, ArrowLeft, Wine, Utensils, Sparkles, MapPin } from "lucide-react";
import { SEO } from "../components/SEO";
import { ShopActions } from "../components/ShopActions";
import { RelatedLinks } from "../components/RelatedLinks";
import { api } from "../lib/api";
import { BIZ } from "../lib/constants";

export default function SpiritDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.product(slug).then(setData).catch(() => setErr(true));
    }, [slug]);

    if (err) return <div className="ls-container py-32 text-center text-white">Product not found. <Link to="/spirits" className="link-underline" style={{ color: "var(--ls-gold)" }}>Browse all</Link></div>;
    if (!data) return <div className="ls-container py-32 text-center" style={{ color: "var(--ls-text-muted)" }}>Loading…</div>;

    const { product: p, related } = data;
    const priceNum = parseFloat((p.price_display || "").replace(/[^0-9.]/g, "")) || 0;
    const jsonLd = {
        "@context": "https://schema.org", "@type": "Product",
        name: p.name, image: p.image_url, description: p.description, brand: { "@type": "Brand", name: p.brand },
        sku: p.slug, category: p.category,
        offers: { "@type": "Offer", url: `${BIZ.siteUrl}/spirits/${p.slug}`, priceCurrency: "USD", price: priceNum, availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", itemCondition: "https://schema.org/NewCondition" },
    };

    return (
        <>
            <SEO title={`${p.name} — ${p.brand}`} description={p.short_description || p.description.slice(0, 160)} path={`/spirits/${p.slug}`} image={p.image_url} jsonLd={jsonLd} type="product" />

            <section style={{ paddingTop: 120 }}>
                <div className="ls-container">
                    <Link to="/spirits" className="inline-flex items-center gap-2 text-sm mb-8" style={{ color: "var(--ls-text-secondary)" }} data-testid="back-to-spirits">
                        <ArrowLeft size={14} /> Back to catalog
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", borderRadius: 6, overflow: "hidden" }}>
                            <img src={p.image_url} alt={p.name} style={{ width: "100%", aspectRatio: "4/5", objectFit: "cover" }} data-testid="product-image" />
                        </div>

                        <div>
                            <Link to={`/spirits/category/${p.category}`} className="pill" data-testid="product-category-link" style={{ textDecoration: "none" }}>
                                {p.category}
                            </Link>
                            <p className="mt-4 text-sm uppercase tracking-widest" style={{ color: "var(--ls-text-muted)" }}>{p.brand}</p>
                            <h1 className="font-display text-white mt-2" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }} data-testid="product-name">
                                {p.name}
                            </h1>
                            <div className="mt-5 flex items-baseline gap-4">
                                <span className="font-bebas" style={{ fontSize: 42, color: "var(--ls-gold)" }} data-testid="product-price">{p.price_display}</span>
                                <span className="text-xs uppercase tracking-wider" style={{ color: "var(--ls-text-muted)" }}>
                                    Display price · check shop for live pricing
                                </span>
                            </div>

                            <p className="mt-6" style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.8 }}>{p.description}</p>

                            <dl className="mt-8 grid grid-cols-2 gap-4 text-sm">
                                {p.abv != null && (<><dt style={{ color: "var(--ls-text-muted)" }}>ABV</dt><dd className="text-white">{p.abv}%</dd></>)}
                                {p.volume_ml && (<><dt style={{ color: "var(--ls-text-muted)" }}>Volume</dt><dd className="text-white">{p.volume_ml} ml</dd></>)}
                                {p.origin && (<><dt style={{ color: "var(--ls-text-muted)" }}>Origin</dt><dd className="text-white">{p.origin}</dd></>)}
                                <dt style={{ color: "var(--ls-text-muted)" }}>Category</dt><dd className="text-white capitalize">{p.category}</dd>
                            </dl>

                            {/* Conversion actions */}
                            <div className="mt-8" data-testid="product-actions-wrap">
                                <ShopActions layout="grid" productName={p.name} testidPrefix="product-actions" showText={false} />
                            </div>

                            {p.tags?.length > 0 && (
                                <div className="mt-8 flex flex-wrap gap-2">
                                    {p.tags.map(t => <span key={t} className="text-xs px-3 py-1 rounded-full" style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border-strong)", color: "var(--ls-text-secondary)" }}>{t}</span>)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* TASTING NOTES — rendered from programmatic enrichment */}
                    {p.tasting_notes && (
                        <section
                            className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-6"
                            data-testid="product-tasting-notes"
                            aria-label="Tasting notes"
                        >
                            {["nose", "palate", "finish"].map((key) => p.tasting_notes[key] && (
                                <div key={key} style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", borderRadius: 6, padding: 28 }}>
                                    <Wine size={20} style={{ color: "var(--ls-gold)" }} aria-hidden="true" />
                                    <h3 className="eyebrow mt-3" style={{ display: "block" }}>{key === "nose" ? "Nose" : key === "palate" ? "Palate" : "Finish"}</h3>
                                    <p className="mt-3 text-white" style={{ fontSize: 15, lineHeight: 1.7 }}>{p.tasting_notes[key]}</p>
                                </div>
                            ))}
                        </section>
                    )}

                    {/* PRODUCER STORY */}
                    {p.producer_story && (
                        <section
                            className="mt-12"
                            data-testid="product-producer-story"
                            style={{
                                background: "linear-gradient(180deg, var(--ls-panel) 0%, #0e0e0e 100%)",
                                border: "1px solid var(--ls-border)",
                                borderRadius: 8,
                                padding: "32px 36px",
                            }}
                        >
                            <span className="eyebrow">From the Producer</span>
                            <h3 className="font-display text-white mt-3" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.2 }}>
                                {p.brand}
                            </h3>
                            {p.producer_location && (
                                <p className="mt-1 text-xs inline-flex items-center gap-1.5" style={{ color: "var(--ls-gold)" }}>
                                    <MapPin size={12} /> {p.producer_location}
                                </p>
                            )}
                            <p className="mt-4" style={{ color: "var(--ls-text-secondary)", fontSize: 15, lineHeight: 1.8 }}>{p.producer_story}</p>
                        </section>
                    )}

                    {/* FOOD PAIRINGS */}
                    {p.food_pairings?.length > 0 && (
                        <section className="mt-12" data-testid="product-food-pairings">
                            <div className="flex items-center gap-3 mb-5">
                                <Utensils size={20} style={{ color: "var(--ls-gold)" }} aria-hidden="true" />
                                <h3 className="font-display text-white" style={{ fontSize: 22, fontWeight: 500 }}>Food pairings</h3>
                            </div>
                            <ul style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, listStyle: "none", padding: 0 }}>
                                {p.food_pairings.map((f, i) => (
                                    <li key={i} style={{ background: "var(--ls-panel)", border: "1px solid var(--ls-border)", borderRadius: 6, padding: "16px 18px", color: "var(--ls-text-secondary)", fontSize: 14, lineHeight: 1.5 }}>
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* COCKTAIL PAIRINGS — links to internal /recipes/:slug */}
                    {p.cocktail_pairings?.length > 0 && (
                        <section className="mt-12" data-testid="product-cocktail-pairings">
                            <div className="flex items-center gap-3 mb-5">
                                <Sparkles size={20} style={{ color: "var(--ls-gold)" }} aria-hidden="true" />
                                <h3 className="font-display text-white" style={{ fontSize: 22, fontWeight: 500 }}>Cocktails to make with this bottle</h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {p.cocktail_pairings.map((c, i) => (
                                    <Link
                                        key={i}
                                        to={`/recipes/${c.slug}`}
                                        className="link-underline"
                                        data-testid={`product-cocktail-${i}`}
                                        style={{
                                            background: "var(--ls-panel)",
                                            border: "1px solid var(--ls-border)",
                                            borderRadius: 6,
                                            padding: "16px 18px",
                                            color: "var(--ls-text)",
                                            fontSize: 15,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <span>{c.name}</span>
                                        <span style={{ color: "var(--ls-gold)", fontSize: 12 }}>Recipe →</span>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {related?.length > 0 && (
                        <div className="mt-24">
                            <h2 className="font-display text-white" style={{ fontSize: 32, fontWeight: 500 }}>You may also like</h2>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-8" data-testid="product-related">
                                {related.map(r => (
                                    <Link key={r.slug} to={`/spirits/${r.slug}`} className="cat-card block" data-testid={`related-${r.slug}`}>
                                        <div style={{ aspectRatio: "4/5", overflow: "hidden" }}>
                                            <img src={r.image_url} alt={r.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-display text-white" style={{ fontSize: 16, fontWeight: 500 }}>{r.name}</h3>
                                            <p className="text-xs mt-1" style={{ color: "var(--ls-text-muted)" }}>{r.brand}</p>
                                            <p className="font-bebas mt-2" style={{ fontSize: 18, color: "var(--ls-gold)" }}>{r.price_display}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* RELATED INTERNAL LINKS */}
                    <RelatedLinks
                        title="Continue exploring"
                        eyebrow="More from Liquor Studio"
                        testid="product-related-links"
                        items={[
                            { label: `All ${p.category}`, to: `/spirits/category/${p.category}`, hint: "Browse the full category" },
                            { label: "Full Spirits Catalog", to: "/spirits", hint: "3,000+ SKUs" },
                            { label: "Cocktail Recipes", to: "/recipes", hint: "From our bar book" },
                            { label: "Visit Our Store", to: "/visit", hint: "309 S 2nd St, Clinton, MO" },
                            { label: "Curbside Pickup", to: "/curbside-pickup", hint: "Order ahead, pull up" },
                            { label: "FAQ", to: "/faq", hint: "Hours, special orders, more" },
                        ]}
                    />
                </div>
                <div style={{ height: 80 }} />
            </section>
        </>
    );
}
